import { useState } from 'react';

// Dummy SVG screen — shown when image fails to load or src missing
function DummyScreen({ bg, fg }) {
  return (
    <svg viewBox="0 0 320 200" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%', display: 'block', position: 'absolute', inset: 0 }}>
      <rect width="320" height="200" fill={bg} />
      <rect x="0" y="0" width="320" height="28" fill={fg} opacity="0.12" />
      {[10, 22, 34].map((x) => <circle key={x} cx={x} cy="14" r="4" fill={fg} opacity="0.4" />)}
      <rect x="50" y="10" width="80" height="8" rx="3" fill={fg} opacity="0.25" />
      <rect x="0" y="28" width="60" height="172" fill={fg} opacity="0.06" />
      {[40, 60, 80, 100, 120].map((y) => <rect key={y} x="8" y={y} width="44" height="6" rx="3" fill={fg} opacity="0.2" />)}
      <rect x="68" y="36" width="120" height="50" rx="4" fill="white" opacity="0.7" />
      <rect x="196" y="36" width="116" height="50" rx="4" fill="white" opacity="0.7" />
      <rect x="68" y="94" width="244" height="30" rx="4" fill="white" opacity="0.7" />
      <rect x="68" y="132" width="244" height="24" rx="4" fill="white" opacity="0.7" />
      {[[74,46,60],[74,56,90],[74,66,45]].map(([x,y,w]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={w} height="5" rx="2" fill={fg} opacity="0.2" />
      ))}
      {[20,32,16,28].map((h, i) => (
        <rect key={i} x={202 + i*20} y={80 - h} width="14" height={h} rx="2" fill={fg} opacity="0.35" />
      ))}
      <rect x="68" y="165" width="64" height="18" rx="4" fill={fg} opacity="0.8" />
      <rect x="76" y="171" width="48" height="6" rx="2" fill="white" opacity="0.9" />
    </svg>
  );
}

// Lightbox modal
function Lightbox({ screens, startIndex, accent, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const [imgError, setImgError] = useState({});

  const screen = screens[current];
  const prev = () => setCurrent((current - 1 + screens.length) % screens.length);
  const next = () => setCurrent((current + 1) % screens.length);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{ position: 'fixed', top: '1.25rem', right: '1.25rem', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', color: 'white', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
      >×</button>

      {/* Content */}
      <div style={{ width: '75vw', maxWidth: '900px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', background: '#fff', position: 'relative', aspectRatio: '16/10' }}>
        {screen.src && !imgError[current] ? (
          <img
            key={current}
            src={screen.src}
            alt={screen.label}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={() => setImgError((e) => ({ ...e, [current]: true }))}
          />
        ) : (
          <DummyScreen bg={screen.bg || '#F0F4FF'} fg={screen.fg || '#3b82f6'} />
        )}
      </div>

      {/* Caption */}
      <div style={{ marginTop: '1rem', fontFamily: "'DM Mono',monospace", fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.06em', textAlign: 'center' }}>
        {screen.label} · {current + 1} / {screens.length}
      </div>

      {/* Dots */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '0.75rem' }}>
        {screens.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            style={{ width: '7px', height: '7px', borderRadius: '50%', background: i === current ? 'white' : 'rgba(255,255,255,0.3)', border: 'none', cursor: 'pointer', transition: 'background 0.2s', padding: 0 }}
          />
        ))}
      </div>

      {/* Arrows */}
      <button onClick={prev} style={{ position: 'fixed', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', color: 'white', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
      <button onClick={next} style={{ position: 'fixed', right: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', color: 'white', fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>→</button>
    </div>
  );
}

// Thumbnail card
function Thumb({ screen, index, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <div
      onClick={() => onClick(index)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', cursor: 'zoom-in', border: '1px solid rgba(0,0,0,0.08)', transition: 'transform 0.2s, box-shadow 0.2s', transform: hovered ? 'translateY(-3px)' : 'none', boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.12)' : 'none' }}
    >
      {/* Aspect ratio box */}
      <div style={{ position: 'relative', width: '100%', paddingTop: '62.5%', background: screen.bg || '#f1f5f9', overflow: 'hidden' }}>
        {screen.src && !imgError ? (
          <img
            src={screen.src}
            alt={screen.label}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s', transform: hovered ? 'scale(1.04)' : 'scale(1)' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <DummyScreen bg={screen.bg || '#F0F4FF'} fg={screen.fg || '#3b82f6'} />
        )}
      </div>

      {/* Caption on hover */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '6px 10px', background: 'linear-gradient(transparent,rgba(0,0,0,0.7))', color: 'white', fontSize: '0.6rem', fontFamily: "'DM Mono',monospace", letterSpacing: '0.04em', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s' }}>
        {screen.label}
      </div>
    </div>
  );
}

// Main Gallery component
export default function Gallery({ screens, accent }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!screens || screens.length === 0) return null;

  return (
    <>
      <div style={{ padding: '2rem 3rem', borderBottom: '1px solid rgba(0,0,0,0.06)', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: accent }}>Design Screens</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.62rem', color: '#aaa', letterSpacing: '0.06em' }}>{screens.length} screens · click to enlarge</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
          {screens.map((screen, i) => (
            <Thumb key={screen.label} screen={screen} index={i} onClick={setLightboxIndex} />
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          screens={screens}
          startIndex={lightboxIndex}
          accent={accent}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
